import { useContext } from 'react';
import {
  centerImage,
  convertRectToViewboxPercentages,
  getMinimumDimensionsFromNaturalDimensionsToCoverArea
} from '@src/utils/wysiwyg_editor/imagePositioning';
import { calculateLocationInViewbox } from './useLocationInViewbox';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { useDispatch } from 'react-redux';
import { saveSnapshot, updateContentItem } from '@src/actions/wysiwyg/editor';
import { TImageContentItem } from '@src/types/wysiwyg_editor';
import type { TPoint, TRect, TSize } from '@src/types/misc';

interface SetImageContentOptions {
  saveSnapshot?: boolean;
}

const defaultSetImageContentOptions: SetImageContentOptions = {
  saveSnapshot: true
};

type SetImageContent = (
  targetPageContentItem: TImageContentItem,
  imageData: {
    url: string;
    naturalDimensions: { height: number; width: number };
  },
  suppliedOptions?: SetImageContentOptions
) => void;

export default function useSetImageContent(): SetImageContent {
  const dispatch = useDispatch();
  const { viewbox } = useContext(WysiwygEditorStateContext);

  return function setImageContent(
    targetPageContentItem: TImageContentItem,
    imageData: {
      url: string;
      naturalDimensions: { height: number; width: number };
    },
    suppliedOptions: SetImageContentOptions = {}
  ) {
    const options = {
      ...defaultSetImageContentOptions,
      ...suppliedOptions
    };
    const clipLocationInViewbox = calculateLocationInViewbox(
      targetPageContentItem.containerLocation,
      viewbox
    );

    const rotatedUrl = imageData.url;
    const minDimensions = getMinimumDimensionsFromNaturalDimensionsToCoverArea(
      clipLocationInViewbox,
      imageData.naturalDimensions
    );

    const centered = centerImage(minDimensions, clipLocationInViewbox);
    const location = convertRectToViewboxPercentages(
      {
        height: minDimensions.height,
        width: minDimensions.width,
        x: centered.x,
        y: centered.y
      },
      viewbox
    );

    if (options.saveSnapshot) {
      dispatch(saveSnapshot());
    }

    dispatch(
      updateContentItem(targetPageContentItem.id, {
        ...targetPageContentItem,
        contentLocation: location,
        href: rotatedUrl,
        naturalDimensions: {
          height: imageData.naturalDimensions.height,
          width: imageData.naturalDimensions.width
        }
      })
    );
  };
}
